import React from 'react';
import Card from '../../components/ui/Card/Card';
import Input from '../../components/ui/Input/Input';
import Button from '../../components/ui/Button/Button';
import Modal from '../../components/ui/Modal/Modal';
import { SearchIcon, InboxIcon, UsersIcon, TruckIcon } from '../../components/ui/Icons';
import { useLayout } from '../../layout/LayoutContext';
import { useClients } from './useClients';
import {
  NO_MODES_SELECTED,
  hasAnyMode,
  ClientModeBadge,
  ClientModeSelector,
  ClientModes,
} from './clientModes';
import ClientsSkeleton from './ClientsSkeleton';
import styles from './Clients.module.css';

const Clients: React.FC = () => {
  const { clients, loading, creating, search, setSearch, createClient, openDetail } = useClients();

  const { setHideHeader } = useLayout();
  React.useEffect(() => {
    setHideHeader(true);
    return () => setHideHeader(false);
  }, [setHideHeader]);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newModes, setNewModes] = React.useState<ClientModes>(NO_MODES_SELECTED);
  const [createErr, setCreateErr] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (createOpen) {
      setNewName('');
      setNewModes(NO_MODES_SELECTED);
      setCreateErr(null);
    }
  }, [createOpen]);

  const submitCreate = async () => {
    const res = await createClient(newName, newModes);
    if (res === true) setCreateOpen(false);
    else setCreateErr(typeof res === 'string' ? res : 'Failed to create client');
  };

  if (loading && clients.length === 0) return <ClientsSkeleton />;

  return (
    <div className={`${styles.page} dylo-animate-in`}>
      <Card className={styles.headerBar}>
        <h1 className={styles.title}>Clients</h1>
        <div className={styles.headerActions}>
          <Button variant="primary" className={styles.newBtn} onClick={() => setCreateOpen(true)}>
            + New Client
          </Button>
          <div className={styles.search}>
            <Input
              icon={<SearchIcon size={17} />}
              placeholder="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {clients.length === 0 ? (
        <Card>
          <p className={styles.empty}>No clients found.</p>
        </Card>
      ) : (
        <div className={styles.grid}>
          {clients.map((c, i) => {
            /**
             * 🔴 El estado del cliente, **cuando el backend lo manda**.
             *
             * Antes era `c.status_id == null || c.status_id === 1`, y `status_id` **no existe en ninguna
             * parte** —ni en la tabla, ni en el modelo, ni en la respuesta—. Así que la condición era
             * `undefined == null` → siempre verdadera → **"Active" para los 206 clientes**, sin ningún
             * dato detrás. Un indicador que no puede cambiar no informa: decora, y encima da confianza
             * en algo que nadie verificó.
             *
             * ⏭️ Mientras no exista el campo real (`SQL_CLIENT_ACTIVE.sql`), no se dibuja nada. Es la
             * decisión honesta: no mostrar el estado es un hueco visible; mostrar uno inventado no.
             */
            const active = c.active ?? (c.status_id != null ? c.status_id === 1 : null);
            return (
              <Card key={c.id} className={styles.clientCard} style={{ animationDelay: `${Math.min(i, 16) * 0.03}s` }}>
                {/*
                  El modo va junto al nombre y no en el pie.
                  🔴 En el pie no cabía: esa pastilla mide unos 165px en una cuadrícula de cinco columnas
                  y ahí ya viven "Active" y el botón, así que la insignia se iba al segundo renglón y
                  crecía el pie de todas las tarjetas. Aquí tiene el ancho completo de la tarjeta, y
                  además el modo es parte de la identidad del cliente — el detalle ya lo muestra junto al
                  título, así que las dos pantallas quedan consistentes.
                */}
                <div className={styles.cardHead}>
                  <h3 className={styles.clientName}>{c.name}</h3>
                  <ClientModeBadge client={c} compact />
                </div>

                <div className={styles.stats}>
                  <span className={styles.stat}>
                    <InboxIcon size={15} />
                    Loads: <b>{c.loadCount ?? 0}</b>
                  </span>
                  <span className={styles.stat}>
                    <UsersIcon size={15} />
                    Users: <b>{c.userCount ?? 0}</b>
                  </span>
                  <span className={styles.stat}>
                    <TruckIcon size={15} />
                    Delivery: <b>{c.deliveryCount ?? 0}</b>
                  </span>
                </div>

                {/*
                  El pie vuelve a tener dos hijos, que es para lo que está pensado su `space-between`:
                  el estatus a la izquierda y la acción a la derecha.
                */}
                <div className={styles.cardFoot}>
                  {active === null ? (
                    // Sin dato no se afirma nada. El hueco se nota y se puede preguntar por él.
                    <span className={styles.statusUnknown}>Status not tracked yet</span>
                  ) : (
                    <span
                      className={[styles.statusDot, active ? styles.active : styles.inactive].join(' ')}
                    >
                      {active ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  <Button variant="primary" className={styles.detailBtn} onClick={() => openDetail(c.id)}>
                    View Detail
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={createOpen}
        title="Create New Client"
        onClose={() => !creating && setCreateOpen(false)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)} disabled={creating}>
              Cancel
            </Button>
            {/*
              🔑 Se exige elegir al menos un modo, y por eso el formulario no preselecciona ninguno.
              Los clientes que ya existían son terrestres, pero eso es un hecho sobre los datos de hoy y
              no una regla para los que vienen: preseleccionar terrestre haría que un cliente aéreo se
              guardara como terrestre sin que nadie lo notara. Sin default y sin exigencia, el camino
              fácil produciría clientes sin modo, que no se ofrecen en ninguna parte.
            */}
            <Button
              variant="primary"
              className={styles.newBtn}
              loading={creating}
              disabled={!newName.trim() || !hasAnyMode(newModes)}
              onClick={submitCreate}
            >
              Create
            </Button>
          </>
        }
      >
        <div className={styles.createForm}>
          <span className={styles.createLabel}>Name</span>
          <Input
            value={newName}
            placeholder="Client name"
            autoFocus
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newName.trim()) submitCreate();
            }}
          />

          <span className={styles.createLabel}>Modes</span>
          <ClientModeSelector value={newModes} onChange={setNewModes} disabled={creating} />
          {/*
            El aviso explica por qué el botón está deshabilitado. Sin esta línea, un botón gris sin
            motivo se lee como que la pantalla está fallando.
          */}
          {!hasAnyMode(newModes) && (
            <span className={styles.createHint}>
              Pick at least one mode. There is no default on purpose: a preselected one would end up
              saved without anyone choosing it.
            </span>
          )}

          {createErr && <span className={styles.createErr}>{createErr}</span>}
        </div>
      </Modal>
    </div>
  );
};

export default Clients;
